import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { initMetaConsentStub, loadMetaPixel } from '../lib/metaPixel';

const COOKIE_CONSENT_KEY = 'yoursetl_cookie_consent';

// Function to load GA4
const loadGA4 = () => {
  if (window.gtag) return; // Already loaded
  
  // Create and append the GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=G-N57WFJ04PF';
  document.head.appendChild(script);
  
  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', 'G-N57WFJ04PF');
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Marketing consent is revoked until the visitor accepts (no Meta scripts loaded)
    initMetaConsentStub();

    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    
    if (consent === 'accepted') {
      // User previously accepted - load GA4 immediately
      loadGA4();
      loadMetaPixel();
    } else if (consent === 'declined') {
      // User previously declined - don't show banner, don't load GA4
      setShowBanner(false);
    } else {
      // No choice made yet - show banner
      setTimeout(() => {
        setShowBanner(true);
        setTimeout(() => setIsVisible(true), 100);
      }, 1000);
      
      // Auto-accept after 5 seconds of continued browsing (implied consent)
      const autoAcceptTimer = setTimeout(() => {
        const currentConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (!currentConsent) {
          // User hasn't explicitly declined, so implied consent
          localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
          loadGA4();
          loadMetaPixel();
          // Hide banner smoothly
          setIsVisible(false);
          setTimeout(() => setShowBanner(false), 300);
        }
      }, 5000);
      
      return () => clearTimeout(autoAcceptTimer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    loadGA4();
    loadMetaPixel();
    setIsVisible(false);
    setTimeout(() => setShowBanner(false), 300);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    if (window.fbq) window.fbq('consent', 'revoke');
    setIsVisible(false);
    setTimeout(() => setShowBanner(false), 300);
  };

  if (!showBanner) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[100] transition-all duration-300 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
    >
      <div className="bg-[#3a2f28] border-t border-[#d4a574]/30 shadow-2xl">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Text */}
            <div className="flex-1 text-center md:text-left">
              <p
                className="text-sm md:text-base text-[#f5f1e8]/90 leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                We use cookies to improve your experience. Continuing to browse means you accept our{' '}
                <Link
                  to="/privacy-policy"
                  className="text-[#d4a574] hover:underline"
                >
                  cookie policy
                </Link>.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleDecline}
                className="px-5 py-2 text-sm text-[#f5f1e8]/80 hover:text-[#f5f1e8] border border-[#f5f1e8]/30 hover:border-[#f5f1e8]/60 rounded transition-all duration-200"
                style={{ fontFamily: 'Inter, sans-serif' }}
                data-testid="cookie-decline-btn"
              >
                Decline
              </button>
              <button
                onClick={handleAccept}
                className="px-5 py-2 text-sm bg-[#d4a574] text-[#3a2f28] hover:bg-[#c49664] rounded font-medium transition-all duration-200"
                style={{ fontFamily: 'Inter, sans-serif' }}
                data-testid="cookie-accept-btn"
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
